import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Users, 
  FileText, 
  MessageSquare, 
  Heart,
  Trash2,
  Shield,
  ShieldOff,
  Eye,
  Clock,
  PenLine,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  Copy,
  Loader2
} from 'lucide-react';

import { API, authFetch } from '../lib/api';

export const AdminPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, name: '' });
  const [deduplicating, setDeduplicating] = useState(false);

  // Sorting & filtering state
  const [articleSort, setArticleSort] = useState('date_desc');
  const [articleFilter, setArticleFilter] = useState('all');
  const [articleAuthorFilter, setArticleAuthorFilter] = useState('all');
  const [articleSearch, setArticleSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    if (!authLoading) {
      if (!user) { navigate('/'); return; }
      checkAdminAndLoad();
    }
  }, [authLoading, user]);

  const checkAdminAndLoad = async () => {
    try {
      const statsRes = await authFetch(`${API}/admin/stats`);
      if (statsRes.status === 403) { toast.error('Admin access required'); navigate('/'); return; }
      if (statsRes.ok) setStats(await statsRes.json());
      await Promise.all([fetchUsers(), fetchArticles()]);
    } catch (error) { console.error('Admin check failed:', error); navigate('/'); }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const res = await authFetch(`${API}/admin/users?limit=200`);
      if (res.ok) setUsers(await res.json());
    } catch (error) { console.error('Failed to fetch users:', error); }
  };

  const fetchArticles = async () => {
    try {
      const res = await authFetch(`${API}/admin/articles?limit=200`);
      if (res.ok) setArticles(await res.json());
    } catch (error) { console.error('Failed to fetch articles:', error); }
  };

  const handleDelete = async () => {
    const { type, id } = deleteDialog;
    try {
      const res = await authFetch(`${API}/admin/${type}/${id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success(`${type === 'users' ? 'User' : 'Article'} deleted`);
        if (type === 'users') setUsers(prev => prev.filter(u => u.user_id !== id));
        else setArticles(prev => prev.filter(a => a.article_id !== id));
        const statsRes = await authFetch(`${API}/admin/stats`);
        if (statsRes.ok) setStats(await statsRes.json());
      } else {
        let msg = 'Delete failed';
        try { const e = await res.json(); msg = e.detail || msg; } catch {}
        toast.error(msg);
      }
    } catch { toast.error('Delete failed'); }
    setDeleteDialog({ open: false, type: null, id: null, name: '' });
  };

  const handleDeduplicate = async () => {
    setDeduplicating(true);
    try {
      const res = await authFetch(`${API}/admin/deduplicate`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        if (data.removed > 0) {
          toast.success(`Removed ${data.removed} duplicate article${data.removed > 1 ? 's' : ''}`);
          fetchArticles();
          const statsRes = await authFetch(`${API}/admin/stats`);
          if (statsRes.ok) setStats(await statsRes.json());
        } else {
          toast.info('No duplicates found');
        }
      }
    } catch { toast.error('Deduplication failed'); }
    setDeduplicating(false);
  };

  const handleToggleAdmin = async (userId) => {
    try {
      const res = await authFetch(`${API}/admin/users/${userId}/admin`, { method: 'PUT' });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, is_admin: data.is_admin } : u));
      } else {
        let msg = 'Failed';
        try { const e = await res.json(); msg = e.detail || msg; } catch {}
        toast.error(msg);
      }
    } catch { toast.error('Failed to update admin status'); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(String(dateString).replace(' ', 'T'));
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Unique authors for filter
  const uniqueAuthors = useMemo(() => {
    const map = {};
    articles.forEach(a => { if (a.author_name) map[a.author_id] = a.author_name; });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [articles]);

  // Duplicates count
  const duplicateCount = useMemo(() => {
    const titles = {};
    articles.forEach(a => {
      const key = `${a.title}__${a.author_id}`;
      titles[key] = (titles[key] || 0) + 1;
    });
    return Object.values(titles).reduce((sum, c) => sum + (c > 1 ? c - 1 : 0), 0);
  }, [articles]);

  // Filtered & sorted articles
  const filteredArticles = useMemo(() => {
    let result = [...articles];

    // Search
    if (articleSearch) {
      const q = articleSearch.toLowerCase();
      result = result.filter(a =>
        a.title?.toLowerCase().includes(q) || a.author_name?.toLowerCase().includes(q)
      );
    }

    // Status filter
    if (articleFilter === 'published') result = result.filter(a => a.is_published);
    else if (articleFilter === 'draft') result = result.filter(a => !a.is_published);

    // Author filter
    if (articleAuthorFilter !== 'all') {
      result = result.filter(a => a.author_id === articleAuthorFilter);
    }

    // Sort
    result.sort((a, b) => {
      switch (articleSort) {
        case 'date_desc': return (b.created_at || '').localeCompare(a.created_at || '');
        case 'date_asc': return (a.created_at || '').localeCompare(b.created_at || '');
        case 'title_asc': return (a.title || '').localeCompare(b.title || '');
        case 'title_desc': return (b.title || '').localeCompare(a.title || '');
        case 'views_desc': return (b.views_count || 0) - (a.views_count || 0);
        case 'claps_desc': return (b.claps_count || 0) - (a.claps_count || 0);
        case 'author_asc': return (a.author_name || '').localeCompare(b.author_name || '');
        default: return 0;
      }
    });

    return result;
  }, [articles, articleSort, articleFilter, articleAuthorFilter, articleSearch]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    if (!userSearch) return users;
    const q = userSearch.toLowerCase();
    return users.filter(u =>
      u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  }, [users, userSearch]);

  // Mark duplicates
  const duplicateTitles = useMemo(() => {
    const counts = {};
    articles.forEach(a => {
      const key = `${a.title}__${a.author_id}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    const set = new Set();
    Object.entries(counts).forEach(([key, c]) => { if (c > 1) set.add(key); });
    return set;
  }, [articles]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <Skeleton className="h-10 w-48 mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>
            Admin Dashboard
          </h1>
          <Badge variant="secondary" className="gap-1"><Shield className="w-3 h-3" /> Admin</Badge>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { icon: Users, value: stats.total_users, label: 'Users' },
              { icon: FileText, value: stats.published_articles, label: 'Published' },
              { icon: MessageSquare, value: stats.total_comments, label: 'Comments' },
              { icon: Heart, value: stats.total_claps, label: 'Claps' },
            ].map(({ icon: Icon, value, label }) => (
              <div key={label} className="p-4 bg-paper rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-accent/10 rounded-lg"><Icon className="w-5 h-5 text-accent" /></div>
                  <div>
                    <p className="text-2xl font-bold">{value}</p>
                    <p className="text-sm text-muted-foreground">{label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="articles">
          <TabsList className="mb-6">
            <TabsTrigger value="articles" className="gap-2" data-testid="admin-articles-tab">
              <FileText className="w-4 h-4" /> Articles ({articles.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2" data-testid="admin-users-tab">
              <Users className="w-4 h-4" /> Users ({users.length})
            </TabsTrigger>
          </TabsList>

          {/* Articles Tab */}
          <TabsContent value="articles">
            {/* Toolbar */}
            <div className="flex flex-wrap gap-3 mb-4 items-center" data-testid="article-toolbar">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search articles..."
                  value={articleSearch}
                  onChange={(e) => setArticleSearch(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="admin-article-search"
                />
              </div>
              <Select value={articleFilter} onValueChange={setArticleFilter}>
                <SelectTrigger className="w-[130px] h-9" data-testid="admin-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Drafts</SelectItem>
                </SelectContent>
              </Select>
              <Select value={articleAuthorFilter} onValueChange={setArticleAuthorFilter}>
                <SelectTrigger className="w-[150px] h-9" data-testid="admin-author-filter">
                  <SelectValue placeholder="Author" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Authors</SelectItem>
                  {uniqueAuthors.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={articleSort} onValueChange={setArticleSort}>
                <SelectTrigger className="w-[160px] h-9" data-testid="admin-sort">
                  <ArrowUpDown className="w-3.5 h-3.5 mr-1" />
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_desc">Newest First</SelectItem>
                  <SelectItem value="date_asc">Oldest First</SelectItem>
                  <SelectItem value="title_asc">Title A-Z</SelectItem>
                  <SelectItem value="title_desc">Title Z-A</SelectItem>
                  <SelectItem value="views_desc">Most Views</SelectItem>
                  <SelectItem value="claps_desc">Most Claps</SelectItem>
                  <SelectItem value="author_asc">Author A-Z</SelectItem>
                </SelectContent>
              </Select>
              {duplicateCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950"
                  onClick={handleDeduplicate}
                  disabled={deduplicating}
                  data-testid="deduplicate-btn"
                >
                  {deduplicating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                  {duplicateCount} duplicate{duplicateCount > 1 ? 's' : ''}
                </Button>
              )}
            </div>

            <p className="text-xs text-muted-foreground mb-3">
              Showing {filteredArticles.length} of {articles.length} articles
            </p>

            <div className="bg-paper rounded-lg border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-articles-table">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="text-left p-3 font-medium text-sm">#</th>
                      <th className="text-left p-3 font-medium text-sm">Title</th>
                      <th className="text-left p-3 font-medium text-sm">Author</th>
                      <th className="text-left p-3 font-medium text-sm">Status</th>
                      <th className="text-left p-3 font-medium text-sm">Date</th>
                      <th className="text-left p-3 font-medium text-sm">Stats</th>
                      <th className="text-left p-3 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredArticles.map((article, idx) => {
                      const isDuplicate = duplicateTitles.has(`${article.title}__${article.author_id}`);
                      return (
                        <tr key={article.article_id} className={`hover:bg-muted/20 ${isDuplicate ? 'bg-amber-50/50 dark:bg-amber-950/20' : ''}`}
                            data-testid={`article-row-${article.article_id}`}>
                          <td className="p-3 text-sm text-muted-foreground">{idx + 1}</td>
                          <td className="p-3 max-w-[300px]">
                            <p className="font-medium text-sm line-clamp-1">{article.title}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                                <Clock className="w-2.5 h-2.5" /> {article.reading_time}m
                              </span>
                              {isDuplicate && (
                                <Badge variant="outline" className="text-[10px] h-4 px-1 text-amber-600 border-amber-300">
                                  Duplicate
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <p className="text-sm">{article.author_name}</p>
                          </td>
                          <td className="p-3">
                            <Badge variant={article.is_published ? "default" : "secondary"} className="text-xs">
                              {article.is_published ? "Published" : "Draft"}
                            </Badge>
                          </td>
                          <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(article.published_at || article.created_at)}
                          </td>
                          <td className="p-3 text-xs text-muted-foreground">
                            <span>{article.views_count || 0} views</span>
                            <span className="ml-2">{article.claps_count || 0} claps</span>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                                onClick={() => navigate(`/article/${article.article_id}`)}
                                title="View"
                                data-testid={`view-article-${article.article_id}`}
                              ><Eye className="w-3.5 h-3.5" /></Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                                onClick={() => navigate(`/write/${article.article_id}`)}
                                title="Edit"
                                data-testid={`edit-article-${article.article_id}`}
                              ><PenLine className="w-3.5 h-3.5" /></Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                onClick={() => setDeleteDialog({
                                  open: true, type: 'articles',
                                  id: article.article_id, name: article.title
                                })}
                                title="Delete"
                                data-testid={`delete-article-${article.article_id}`}
                              ><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredArticles.length === 0 && (
                      <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No articles match your filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="mb-4">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="admin-user-search"
                />
              </div>
            </div>
            <div className="bg-paper rounded-lg border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-users-table">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="text-left p-3 font-medium text-sm">User</th>
                      <th className="text-left p-3 font-medium text-sm">Email</th>
                      <th className="text-left p-3 font-medium text-sm">Articles</th>
                      <th className="text-left p-3 font-medium text-sm">Joined</th>
                      <th className="text-left p-3 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredUsers.map(u => (
                      <tr key={u.user_id} className="hover:bg-muted/20" data-testid={`user-row-${u.user_id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarImage src={u.picture} alt={u.name} />
                              <AvatarFallback className="text-xs">{getInitials(u.name)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-sm">{u.name}</p>
                              {u.is_admin && <Badge variant="secondary" className="text-[10px] mt-0.5">Admin</Badge>}
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">{u.email}</td>
                        <td className="p-3 text-sm">{u.articles_count || 0}</td>
                        <td className="p-3 text-xs text-muted-foreground">{formatDate(u.created_at)}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                              onClick={() => navigate(`/profile/${u.user_id}`)} title="View Profile"
                            ><Eye className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                              onClick={() => handleToggleAdmin(u.user_id)}
                              title={u.is_admin ? "Remove Admin" : "Make Admin"}
                              disabled={u.user_id === user?.user_id}
                            >{u.is_admin ? <ShieldOff className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}</Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                              onClick={() => setDeleteDialog({ open: true, type: 'users', id: u.user_id, name: u.name })}
                              disabled={u.user_id === user?.user_id} title="Delete User"
                            ><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ ...deleteDialog, open: false })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteDialog.type === 'users' ? 'User' : 'Article'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialog.name}"? This action cannot be undone.
              {deleteDialog.type === 'users' && ' All their articles, comments, and data will be permanently deleted.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPage;
